"use client";

/**
 * Cropped real share-sheet screenshot showing Mənzil.ai in the apps
 * grid. Picks between two variants based on visitor UA:
 *   - iOS   → share-sheet-ios.png     (Safari Share Sheet)
 *   - else  → share-sheet-android.png (Chrome Share Sheet)
 *
 * Reused across:
 *   - /install (Share-target teaser section)
 *   - PWAWelcomeModal (first-launch teaching moment, PWA users only)
 *
 * Falls back to the Android variant during SSR / pre-hydration; the
 * iOS variant swaps in client-side. Either is accurate enough to
 * convey the pitch ("tap Share → Mənzil.ai") if the other flashes
 * briefly.
 */
import { useEffect, useState } from "react";
import { useT } from "@/lib/locale-context";

type Variant = "ios" | "android";

function detectVariant(): Variant {
  if (typeof navigator === "undefined") return "android";
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) ||
                (ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document);
  return isIOS ? "ios" : "android";
}

export default function PhoneShareSheetIllustration({
  className = "mx-auto w-full max-w-xs rounded-2xl border border-gray-200 shadow-sm",
}: { className?: string }) {
  const t = useT();
  const [variant, setVariant] = useState<Variant>("android");

  useEffect(() => {
    setVariant(detectVariant());
  }, []);

  const src = variant === "ios"
    ? "/install/share-sheet-ios.png"
    : "/install/share-sheet-android.png";

  // Intrinsic sizes differ: iOS is 1125x1320, Android is 540x445.
  // Width/height attrs help the browser reserve the correct aspect
  // ratio slot before the image decodes - prevents CLS on slow links.
  const dims = variant === "ios"
    ? { width: 1125, height: 1320 }
    : { width: 540, height: 445 };

  return (
    <img
      src={src}
      alt={t("a11y.share_sheet_alt")}
      {...dims}
      className={className}
      loading="lazy"
      decoding="async"
    />
  );
}
